export const profile = {
  name: "Sahithi Rasakonda",
  firstName: "Sahithi",
  lastName: "Rasakonda",
  location: "Dortmund, Germany",
  email: "sahithiraj1206@gmail.com",
  phone: "+49 176 451 2277",
  linkedin: "https://www.linkedin.com/in/sahithi-rasakonda-4796a7217/",
  roles: [
    "Master's Student in Psychology & Management",
    "Aspiring Industrial & Organizational Psychologist",
    "Research Enthusiast",
    "Creative Problem Solver",
  ],
  statement:
    "I study what makes people thrive — in clinics, in classrooms, and in the organizations they build. My path runs from paediatric psychology wards in Hyderabad to management lecture halls in Dortmund, and it is guided by one belief: evidence and empathy belong together.",
  portrait: "/photos/hero-sunset.webp",
  cvUrl: "/Sahithi_Rasakonda_CV.pdf",
};

export const exploring = [
  {
    icon: "BookOpen",
    label: "Master's Thesis",
    title: "Human–AI Interaction in the Workplace",
    text: "Developing a mixed-methods study on trust, motivation, and meaning when your colleague is an algorithm.",
  },
  {
    icon: "Library",
    label: "On the Nightstand",
    title: "Currently Reading",
    text: "Thinking, Fast and Slow — Kahneman · Work Rules! — Bock · The Culture Code — Coyle.",
  },
  {
    icon: "GraduationCap",
    label: "Certifications in Progress",
    title: "Deepening the Toolkit",
    text: "Advanced statistics for behavioural science and people-analytics fundamentals.",
  },
  {
    icon: "FlaskConical",
    label: "Research Interests",
    title: "Where My Curiosity Lives",
    text: "Workplace wellbeing, human–AI collaboration, selection & assessment, organizational culture.",
  },
  {
    icon: "Sparkles",
    label: "Skills I'm Learning",
    title: "Always a Student",
    text: "German language, R for data analysis, and the quiet art of better interviewing.",
  },
  {
    icon: "Compass",
    label: "Career Goals",
    title: "Where I'm Headed",
    text: "An I/O psychology role in organizational development — building workplaces where people do their best work.",
  },
];

export const about = {
  lead: "The shortest version: I am a psychologist in training who fell in love with organizations.",
  paragraphs: [
    "My story begins in Hyderabad, India, where I studied Psychology, Mass Communication, and Literature — three lenses on the same subject: people. Psychology gave me the science, communication gave me the voice, and literature gave me the stories. I have been weaving them together ever since.",
    "The deciding chapter was a year-long clinical internship at Rainbow Children's Hospital. Sitting with families, conducting assessments, and writing case histories taught me that behaviour is never random — it is communication, waiting for someone trained to listen. It also taught me the discipline of evidence: every insight had to survive supervision, diagnostics, and data.",
    "So why management? Because the same questions that fill a clinic — motivation, trust, development, wellbeing — fill every workplace. I moved to Germany to study Psychology & Management at the International School of Management, and spent an exchange semester at St. Francis College in New York, learning how culture reshapes everything we assume about people.",
    "What motivates me is simple: work occupies most of our waking lives, and it should be a place where people grow rather than merely endure. I want to help build those places — with rigor, with warmth, and with a researcher's respect for the evidence.",
  ],
  values: ["Curiosity", "Empathy", "Evidence", "Creativity", "Ambition"],
};

export const education = [
  {
    period: "2024 — Present",
    degree: "M.Sc. Psychology and Management",
    institution: "International School of Management (ISM) — Germany",
    description:
      "Where psychology meets the boardroom. My focus areas span organizational behavior, leadership, cognitive processes, statistical analysis, and human–AI interaction — the intersection where my thesis is taking shape.",
    tags: ["Organizational Behavior", "Leadership", "Research Methods", "Project Management", "Statistics"],
  },
  {
    period: "Fall 2025",
    degree: "Exchange Semester — Psychology & Business",
    institution: "St. Francis College — New York, USA",
    description:
      "A semester in Brooklyn that turned cross-cultural communication from a course topic into a daily practice. New York taught me to think on my feet and to question everything I assumed was universal about people.",
    tags: ["Cross-Cultural Communication", "International Perspective"],
  },
  {
    period: "2023",
    degree: "Diploma in Organizational Psychology",
    institution: "Cognizavest",
    description:
      "My first formal step into the world of work psychology — the bridge between my clinical foundations and my future in organizations.",
    tags: ["Organizational Psychology"],
  },
  {
    period: "2019 — 2022",
    degree: "B.A. Psychology, Mass Communication & Literature",
    institution: "Villa Marie Degree College — Hyderabad, India",
    description:
      "Three disciplines, one subject: people. This is where the scientist, the communicator, and the storyteller in me first met.",
    tags: ["Psychology", "Mass Communication", "Literature"],
  },
];

export const experience = [
  {
    role: "Clinical Psychologist Intern",
    org: "Rainbow Children's Hospital — Hyderabad, India",
    period: "Jul 2023 — Jun 2024",
    summary:
      "A one-year intensive paediatric internship within a multidisciplinary clinical team.",
    learned:
      "I learned that the assessment begins long before the test does. Conducting IQ and developmental assessments, ADHD screenings, CARS evaluations, and SLD profiles taught me psychometric rigor — but sitting with families taught me listening. I left able to turn observations into individualized intervention plans, and data into decisions families could trust.",
    impact:
      "Contributed to diagnostic case conferences, prepared clinical reports, and volunteered at the National Conference on Autism Spectrum Disorder, coordinating with psychologists, psychiatrists, and therapists.",
    skills: ["Psychometric Assessment", "Clinical Reporting", "Case Management", "Family Counselling"],
  },
  {
    role: "Social Media Marketing & Community Engagement Intern",
    org: "Digital Outreach — Hyderabad, India",
    period: "2023",
    summary: "Designing digital campaigns that put psychology to work in communication.",
    learned:
      "I learned that engagement is applied psychology. Every campaign was a small experiment in attention, motivation, and message design — and collaborating across teams taught me how communication strategy is really people strategy.",
    impact: "Improved community engagement and brand visibility through evidence-informed outreach campaigns.",
    skills: ["Communication Strategy", "Community Engagement", "Cross-functional Collaboration"],
  },
  {
    role: "Cognitive Rehabilitation Intern",
    org: "ARDSI — Hyderabad Deccan Chapter",
    period: "2022",
    summary: "Supporting dementia-care programs through structured cognitive rehabilitation.",
    learned:
      "Working with dementia patients redefined progress for me: small, dignified victories that demand patience and precision in equal measure. Designing therapy activities alongside senior psychologists showed me what empathy looks like when it becomes method.",
    impact: "Awarded Grade A for outstanding performance and empathy-based support.",
    skills: ["Cognitive Rehabilitation", "Therapy Design", "Progress Evaluation"],
  },
  {
    role: "Counseling Psychology Intern",
    org: "Psychoflakes",
    period: "2020",
    summary: "First supervised steps in counselling practice and case analysis.",
    learned:
      "My first supervised counselling sessions taught me the discipline of presence — and that asking the right question is a skill you train like any other. Group interventions sharpened my applied problem-solving and interpersonal range.",
    impact: "Conducted supervised sessions, case analyses, and group intervention presentations.",
    skills: ["Counselling", "Case Analysis", "Group Intervention"],
  },
];

export const projects = [
  {
    num: "01",
    title: "Human–AI Interaction in the Modern Workplace",
    kind: "Master's Thesis — In Progress",
    overview:
      "An investigation into the psychological contract between people and intelligent systems at work: what happens to trust, motivation, and meaning when your colleague is an algorithm?",
    objective:
      "To understand how AI adoption shapes the lived experience of work — beyond productivity metrics, into autonomy, trust, and purpose.",
    methodology: "Mixed-methods design: quantitative survey at scale, followed by semi-structured interviews for depth.",
    tools: ["SPSS", "Survey Design", "Interview Protocols", "Literature Synthesis"],
    outcome:
      "Thesis proposal developed; research design and literature framework underway at ISM.",
    lesson:
      "The best research questions come from listening to what practitioners are actually worried about.",
  },
  {
    num: "02",
    title: "Early Identification of Developmental Needs in Children",
    kind: "Clinical Case Series — Rainbow Children's Hospital",
    overview:
      "A year-long applied study in paediatric psychological assessment, spanning IQ and developmental testing, ADHD screening, autism rating (CARS), and specific learning disability profiles.",
    objective:
      "To translate psychometric evidence into intervention plans families and clinicians could act on together.",
    methodology:
      "Standardized psychometric batteries, structured case histories, diagnostic case conferences under supervision.",
    tools: ["IQ/DQ Assessment", "CARS", "ADHD Screening", "Clinical Reporting"],
    outcome:
      "Dozens of assessments and clinical reports contributing to individualized intervention plans.",
    lesson: "Data earns trust only when it is delivered with empathy.",
  },
  {
    num: "03",
    title: "National Conference on Autism Spectrum Disorder",
    kind: "Research Engagement & Volunteering",
    overview:
      "Two days inside India's conversation on autism — coordinating sessions and engaging directly with the psychologists, psychiatrists, and therapists shaping the field.",
    objective:
      "To deepen my understanding of spectrum research and intervention beyond the classroom.",
    methodology: "Event coordination, professional dialogue, and structured observation of research presentations.",
    tools: ["Event Coordination", "Professional Networking", "Research Synthesis"],
    outcome:
      "Firsthand exposure to current ASD research and intervention practice at a national level.",
    lesson: "A field moves forward in conversations, not just publications.",
  },
  {
    num: "04",
    title: "Digital Outreach & Community Engagement",
    kind: "Applied Communication Project",
    overview:
      "A digital campaign practice treating community engagement as applied psychology — designing outreach around how people actually attend, decide, and connect.",
    objective:
      "To strengthen brand visibility and community engagement through evidence-informed communication.",
    methodology: "Campaign design, content experimentation, cross-functional collaboration, reach analysis.",
    tools: ["Content Strategy", "Analytics", "Google Workspace"],
    outcome: "Measurably improved engagement and online reach for the community programs supported.",
    lesson: "Communication strategy is people strategy.",
  },
];

export const skillGroups = [
  {
    title: "Psychology & Clinical Practice",
    span: "wide",
    items: [
      "Psychometric Assessment",
      "IQ & Developmental Testing",
      "Evidence-Based Intervention",
      "Case History & Reporting",
      "Cognitive Rehabilitation",
      "Counselling",
    ],
  },
  {
    title: "Research & Statistics",
    span: "tall",
    items: ["Research Methodology", "Statistical Analysis", "SPSS", "Data Analysis", "Report Writing"],
  },
  {
    title: "Organizational & Management",
    span: "square",
    items: ["Organizational Behavior", "Leadership", "Project Planning", "Negotiation & Conflict Resolution"],
  },
  {
    title: "Communication & Leadership",
    span: "square",
    items: ["Public Speaking", "Active Listening", "Cross-Cultural Communication", "Team Coordination"],
  },
  {
    title: "Languages",
    span: "square",
    items: ["English — Advanced", "Telugu — Native", "Hindi — Bilingual", "German — Beginner", "French — Elementary"],
  },
  {
    title: "Digital Tools",
    span: "wide",
    items: ["MS Office", "Google Workspace", "AI Productivity Tools", "SPSS"],
  },
];

export const certificates = [
  { title: "Solution Focused Brief Therapy (SFBT)", issuer: "Certification", year: "Clinical Training" },
  { title: "Statistics & Data Analysis", issuer: "Certification", year: "Research Training" },
  { title: "Rorschach Inkblot Test Training", issuer: "Workshop", year: "Assessment" },
  { title: "AMI Adolescent Introductory Workshop", issuer: "Workshop", year: "Developmental" },
  { title: "SuJok Therapy — Level 1", issuer: "Certification", year: "Complementary Practice" },
  { title: "BSS Diploma in Acupuncture & Naturopathy", issuer: "Diploma", year: "Complementary Practice" },
];

export const achievements = [
  {
    title: "Grade A — Outstanding Performance",
    detail: "Awarded for empathy-based support during cognitive rehabilitation work at ARDSI.",
  },
  {
    title: "1st Prize in Photography",
    detail: "Jupiter Academy of Audio Visual Communications, during a film-making course.",
  },
  {
    title: "Community Wellness Volunteer",
    detail: "Multiple community-wellness and mental-health awareness programs.",
  },
];

export const interests = [
  {
    title: "Reading",
    slug: "reading",
    text: "Behavioural science, novels, and anything that explains people.",
    image: "/photos/bookshop-nyc.webp",
  },
  {
    title: "Photography",
    slug: "photography",
    text: "An award-winning habit of noticing — light, faces, quiet moments.",
    image: "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    title: "Traveling",
    slug: "traveling",
    text: "From Hyderabad to Dortmund to New York — cultures are my field studies.",
    image: "/photos/brooklyn-bridge.webp",
  },
  {
    title: "Journaling",
    slug: "journaling",
    text: "Where reflections become research questions.",
    image: "https://images.unsplash.com/photo-1586380951230-e6703d9f6833?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    title: "Coffee Culture",
    slug: "coffee-culture",
    text: "Every city I've lived in, remembered in cafés.",
    image: "/photos/coffee-gimbal.webp",
  },
  { title: "Personal Growth", slug: "growth", text: "Treating myself as a lifelong pilot study.", icon: "Sprout" },
];

export const gallery = [
  {
    src: "/photos/train-journey.webp",
    caption: "Window seats and wandering thoughts",
    tag: "Travel",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/cafe-evening.jpeg",
    caption: "An evening well spent",
    tag: "Everyday",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/snow-day.webp",
    caption: "Snow day in New York",
    tag: "Travel",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/nyc-nights.webp",
    caption: "Manhattan after dark",
    tag: "Travel",
    ratio: "aspect-[4/3]",
  },
  {
    src: "/photos/autumn-walk.webp",
    caption: "Autumn underfoot",
    tag: "Everyday",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/budapest-parliament.webp",
    caption: "Budapest, dressed in gold",
    tag: "Travel",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/grand-central.webp",
    caption: "A station like a cathedral",
    tag: "Travel",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/bookshop-nyc.webp",
    caption: "Books, floating",
    tag: "Creative",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/nordic-road.webp",
    caption: "Sunset at the edge of the map",
    tag: "Travel",
    ratio: "aspect-[4/3]",
  },
  {
    src: "/photos/lighthouse-sea.webp",
    caption: "The lighthouse at the edge",
    tag: "Travel",
    ratio: "aspect-[4/3]",
  },
  {
    src: "/photos/museum-day.webp",
    caption: "Museum hours",
    tag: "Creative",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/bookshop-curiosities.webp",
    caption: "Half bookshop, half universe",
    tag: "Creative",
    ratio: "aspect-[3/4]",
  },
  {
    src: "/photos/library-steps.webp",
    caption: "Library steps",
    tag: "Everyday",
    ratio: "aspect-[3/4]",
  },
];

export const marqueeItems = [
  "Psychology & Management",
  "Human-Centred Research",
  "Organizational Behavior",
  "Evidence & Empathy",
  "Curiosity, Always",
];

export const marqueeDark = [
  "Research in progress",
  "Human–AI Interaction",
  "Workplace Wellbeing",
  "Evidence & Empathy",
];
